import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import QuestionMarkOutlinedIcon from "@mui/icons-material/QuestionMarkOutlined";
import {
  CmpInvoiceDetailFieldsFragment,
  CmpInvoiceDisplayStateEnum,
  CmpInvoiceKindEnum,
  CmpInvoicePaymentRuleKindEnum,
  CmpManageInvoiceRowFieldsFragment,
  GetUserQuery,
} from "../../../../graphql/types";
import { commonStyles } from "../../../common/commonStyles";
import {
  formatCurrency,
  formatDate,
  formatOrdinalLongDate,
  getInvoiceDocumentTitle,
  getInvoicePreviewActions,
  getInvoiceWatermark,
  getRecordedPaymentMethodLabel,
  InvoicePreviewAction,
} from "../invoiceFormatting";

type InvoiceRow = CmpManageInvoiceRowFieldsFragment;
type InvoiceDetail = CmpInvoiceDetailFieldsFragment;
type ChamberInfo = GetUserQuery["getUser"]["chamberInfo"];

export type RecordInvoicePaymentInput = {
  amountInCents: number;
  note?: string;
  paymentMethodLabel: string;
  receivedAtUtcMilli?: number;
  reference?: string;
};

const invoiceBlue = "#1684FF";
const invoiceGreen = "#16B983";
const invoiceRed = "#F31723";
const creditCardPaymentMethodLabel = "Credit Card";

type PaymentMethodOption = {
  displayLabel: string;
  isCreditCard: boolean;
  mutationLabel: string;
};

const formatDateInput = (utcMilli?: number | null) => {
  if (!utcMilli) {
    return new Date().toISOString().slice(0, 10);
  }

  return new Date(utcMilli).toISOString().slice(0, 10);
};

const dateInputToUtcMilli = (value: string) =>
  value ? new Date(`${value}T12:00:00`).getTime() : undefined;

const formatCurrencyInput = (amountInCents: number) =>
  (amountInCents / 100).toFixed(2);

const parseCurrencyInput = (value: string) =>
  Math.max(0, Math.round((Number(value.replace(/[^0-9.]/g, "")) || 0) * 100));

const normalizeManualMethodLabel = (label: string) => {
  const normalized = label.trim().toLowerCase();

  if (normalized.includes("direct")) {
    return "Direct Deposit";
  }

  if (normalized.includes("transfer")) {
    return "E-Transfer";
  }

  if (normalized.includes("cheque") || normalized.includes("check")) {
    return "Cheque";
  }

  if (normalized.includes("cash")) {
    return "Cash";
  }

  return label.trim();
};

const getInvoiceLineItems = (detail?: InvoiceDetail | null) => {
  if (detail?.lineItems?.length) {
    return detail.lineItems;
  }

  return detail?.invoice.lineItems || [];
};

const sumLineItems = (
  detail: InvoiceDetail | null | undefined,
  field: "subtotalAmountInCents" | "taxAmountInCents" | "totalAmountInCents"
) => getInvoiceLineItems(detail).reduce((total, item) => total + item[field], 0);

const getInvoiceSubtotal = (detail?: InvoiceDetail | null, row?: InvoiceRow | null) =>
  detail?.invoice.subtotalAmountInCents ?? sumLineItems(detail, "subtotalAmountInCents") ??
  row?.totalAmountInCents ?? 0;

const getInvoiceTax = (detail?: InvoiceDetail | null) =>
  detail?.invoice.taxAmountInCents ?? sumLineItems(detail, "taxAmountInCents") ?? 0;

const getInvoiceTotal = (detail?: InvoiceDetail | null, row?: InvoiceRow | null) =>
  detail?.invoice.totalAmountInCents ??
  sumLineItems(detail, "totalAmountInCents") ??
  row?.totalAmountInCents ??
  0;

const getAmountDue = (detail?: InvoiceDetail | null, row?: InvoiceRow | null) =>
  detail?.invoice.amountDueInCents ?? row?.balanceDueInCents ?? 0;

const getPaymentPlan = (detail?: InvoiceDetail | null, row?: InvoiceRow | null) =>
  detail?.invoice.paymentPlan || row?.paymentPlan || null;

const getPaymentRules = (detail?: InvoiceDetail | null, row?: InvoiceRow | null) =>
  detail?.invoice.paymentRules || row?.paymentRules || null;

const getBillingSnapshot = (detail?: InvoiceDetail | null) =>
  detail?.billingSnapshot || detail?.invoice.billingSnapshot || null;

const getBillToSnapshot = (detail?: InvoiceDetail | null) =>
  detail?.invoice.billToSnapshot || getBillingSnapshot(detail);

const getAddressLines = (
  snapshot:
    | NonNullable<InvoiceDetail["billingSnapshot"]>
    | NonNullable<InvoiceDetail["invoice"]["billToSnapshot"]>
    | null
    | undefined
) =>
  [
    snapshot?.addressLine1,
    snapshot?.addressLine2,
    [snapshot?.city, snapshot?.province, snapshot?.postalCode].filter(Boolean).join(" "),
    snapshot?.country,
  ].filter(Boolean);

const getChamberAddressLines = (chamber?: ChamberInfo) =>
  [
    chamber?.address?.streetAddress,
    [chamber?.address?.city, chamber?.address?.province, chamber?.address?.postalCode]
      .filter(Boolean)
      .join(" "),
  ].filter(Boolean);

const getStatusLabel = (state?: CmpInvoiceDisplayStateEnum | null, row?: InvoiceRow | null) => {
  if (state === CmpInvoiceDisplayStateEnum.Draft) {
    return "Draft";
  }

  if (state === CmpInvoiceDisplayStateEnum.Paid) {
    return "Paid";
  }

  // The document pill reflects the pre-reversal/pre-void state (the watermark
  // conveys REVERSED / CANCELLED), matching the design samples: a reversed
  // invoice still reads "Paid", a voided invoice falls through to "Outstanding".
  if (state === CmpInvoiceDisplayStateEnum.Refunded) {
    return "Paid";
  }

  if (state === CmpInvoiceDisplayStateEnum.Waived) {
    return "Waived";
  }

  if (state === CmpInvoiceDisplayStateEnum.NotPaid && row?.daysPastDue && row.daysPastDue > 0) {
    return "Overdue";
  }

  return "Outstanding";
};

const getNextPaymentAmount = (detail?: InvoiceDetail | null, row?: InvoiceRow | null) => {
  const plan = getPaymentPlan(detail, row);

  return plan?.nextInstallmentAmountInCents || getAmountDue(detail, row);
};

const formatTaxRatePercentage = (ratePercentage: number) =>
  String(Math.round(ratePercentage * 100) / 100);

const getInvoiceTaxRatePercentage = (
  detail?: InvoiceDetail | null,
  row?: InvoiceRow | null
) => {
  const lineItemTaxRates = Array.from(
    new Set(
      getInvoiceLineItems(detail)
        .map((item) => item.taxRatePercentage)
        .filter((rate) => Number.isFinite(rate) && rate > 0)
        .map((rate) => Math.round(rate * 10000) / 10000)
    )
  );

  if (lineItemTaxRates.length === 1) {
    return lineItemTaxRates[0];
  }

  const subtotal = getInvoiceSubtotal(detail, row);
  const tax = getInvoiceTax(detail);

  if (!subtotal || !tax) {
    return 0;
  }

  return (tax / subtotal) * 100;
};

const getInvoiceTaxLabel = (
  detail?: InvoiceDetail | null,
  row?: InvoiceRow | null
) => {
  const taxRatePercentage = getInvoiceTaxRatePercentage(detail, row);

  return taxRatePercentage > 0
    ? `Tax (${formatTaxRatePercentage(taxRatePercentage)}%)`
    : "Tax";
};

const getTaxRateRatio = (detail?: InvoiceDetail | null, row?: InvoiceRow | null) => {
  const subtotal = getInvoiceSubtotal(detail, row);
  const tax = getInvoiceTax(detail);

  if (!subtotal || !tax) {
    return 0;
  }

  return tax / subtotal;
};

const splitAmountByTaxRatio = (
  amountInCents: number,
  detail?: InvoiceDetail | null,
  row?: InvoiceRow | null
) => {
  const ratio = getTaxRateRatio(detail, row);

  if (!ratio) {
    return { subtotalInCents: amountInCents, taxInCents: 0 };
  }

  const subtotalInCents = Math.round(amountInCents / (1 + ratio));

  return {
    subtotalInCents,
    taxInCents: amountInCents - subtotalInCents,
  };
};

const buildPaymentMethodOptions = (
  detail?: InvoiceDetail | null,
  row?: InvoiceRow | null
): PaymentMethodOption[] => {
  const rules = getPaymentRules(detail, row);
  const options = [
    rules?.creditCardAllowed
      ? {
          displayLabel: creditCardPaymentMethodLabel,
          isCreditCard: true,
          mutationLabel: creditCardPaymentMethodLabel,
        }
      : null,
    ...(rules?.allowedManualPaymentMethodLabels || []).map((label) => ({
      displayLabel: normalizeManualMethodLabel(label),
      isCreditCard: false,
      mutationLabel: label,
    })),
  ].filter(Boolean) as PaymentMethodOption[];
  const optionsByDisplayLabel = new Map<string, PaymentMethodOption>();

  options.forEach((option) => {
    if (!optionsByDisplayLabel.has(option.displayLabel)) {
      optionsByDisplayLabel.set(option.displayLabel, option);
    }
  });

  return Array.from(optionsByDisplayLabel.values());
};

const getPaymentOptionCards = (
  detail?: InvoiceDetail | null,
  row?: InvoiceRow | null,
  chamber?: ChamberInfo
) => {
  const rules = getPaymentRules(detail, row);

  if (rules?.ruleKind === CmpInvoicePaymentRuleKindEnum.InsuranceDirectDepositOnly) {
    return [
      {
        label: "Direct Deposit",
        body: "Please use the group insurance direct deposit process. Staff must manually confirm this payment.",
      },
    ];
  }

  const chamberName = chamber?.name || "the chamber";
  const chamberEmail = chamber?.supportContactEmail || "the chamber";
  const invoiceNumber = row?.invoiceNumber || detail?.invoice.invoiceNumber || "";
  const mailingAddress = getChamberAddressLines(chamber).join(", ");

  return buildPaymentMethodOptions(detail, row).map((method) => {
    const label = method.displayLabel;

    if (label === "Credit Card") {
      return {
        label: "Pay Credit Card",
        body: "Pay securely online or call the chamber to pay by credit card.",
        cta: "Pay Now",
      };
    }

    if (label === "E-Transfer") {
      return {
        label: "E-Transfer",
        body: `Send payment to:\n${chamberEmail}${
          invoiceNumber ? `\nReference: ${invoiceNumber}` : ""
        }`,
      };
    }

    if (label === "Cheque") {
      return {
        label: "Cheque",
        body: `Make payable to:\n${chamberName}${
          mailingAddress ? `\nMail or deliver to:\n${mailingAddress}` : ""
        }`,
      };
    }

    if (label === "Direct Deposit") {
      return {
        label: "Direct Deposit",
        body: "Please contact the chamber for direct deposit instructions.",
      };
    }

    if (label === "Cash") {
      return {
        label: "Cash",
        body: "Please contact the chamber to arrange cash payment.",
      };
    }

    return {
      label,
      body: "Please contact the chamber to arrange this payment method.",
    };
  });
};

const InvoiceActionButton = ({
  children,
  color = "gold",
  compact = false,
  disabled,
  icon,
  onClick,
}: {
  children: React.ReactNode;
  color?: "gold" | "green";
  // INV-PREVIEW-001: the Print PDF action renders as a smaller pill in the
  // preview action bar to match the mock, while approve/send buttons keep the
  // larger emphasis size.
  compact?: boolean;
  disabled?: boolean;
  icon?: React.ReactNode;
  onClick: () => void;
}) => (
  <Button
    disabled={disabled}
    onClick={onClick}
    startIcon={icon}
    sx={{
      backgroundColor: color === "green" ? invoiceGreen : commonStyles.cpaGold,
      borderRadius: "4px",
      boxShadow: "0 2px 5px rgba(15, 23, 42, 0.18)",
      color: "white",
      fontSize: compact ? 14 : 16,
      fontWeight: compact ? 700 : 800,
      minHeight: compact ? 40 : 48,
      px: compact ? 2.25 : 3.5,
      textTransform: "none",
      "&:hover": {
        backgroundColor: color === "green" ? "#12A875" : "#C88B16",
      },
    }}
  >
    {children}
  </Button>
);

const SnapshotBlock = ({
  label,
  snapshot,
  fallbackName,
}: {
  fallbackName: string;
  label: string;
  snapshot:
    | NonNullable<InvoiceDetail["billingSnapshot"]>
    | NonNullable<InvoiceDetail["invoice"]["billToSnapshot"]>
    | null
    | undefined;
}) => (
  <div className="min-w-0 break-words text-[16px] leading-7 text-[#111827]">
    <div className="mb-4 text-[12px] font-extrabold uppercase tracking-wide">
      {label}
    </div>
    <div className="font-extrabold">{snapshot?.customerName || fallbackName}</div>
    {snapshot?.contactName ? <div>Attn: {snapshot.contactName}</div> : null}
    {getAddressLines(snapshot).map((line) => (
      <div key={line}>{line}</div>
    ))}
    {snapshot?.contactEmail ? <div className="mt-3">{snapshot.contactEmail}</div> : null}
    {snapshot?.contactPhone ? <div>{snapshot.contactPhone}</div> : null}
  </div>
);

// Exported per SCR-MC-01 so the Create Member invoice preview can reuse the
// master invoice document (PAR-MC-32 cross-file parity, module 10).
export const InvoicePaper = ({
  chamber,
  detail,
  row,
}: {
  chamber?: ChamberInfo;
  detail?: InvoiceDetail | null;
  row: InvoiceRow;
}) => {
  const billingSnapshot = getBillingSnapshot(detail);
  const billToSnapshot = getBillToSnapshot(detail);
  const lineItems = getInvoiceLineItems(detail);
  const subtotal = getInvoiceSubtotal(detail, row);
  const tax = getInvoiceTax(detail);
  const taxLabel = getInvoiceTaxLabel(detail, row);
  const total = getInvoiceTotal(detail, row);
  const amountDue = getAmountDue(detail, row);
  const resolvedDisplayState = detail?.displayState || row.displayState;
  const statusLabel = getStatusLabel(resolvedDisplayState, row);
  const watermark = getInvoiceWatermark(resolvedDisplayState);
  // INV-TABLE-002: in-flow banner shown above the line items for terminal states.
  const statusBanner =
    resolvedDisplayState === CmpInvoiceDisplayStateEnum.Voided
      ? {
          title: "Cancelled",
          description: "This invoice has been cancelled and is no longer payable.",
        }
      : resolvedDisplayState === CmpInvoiceDisplayStateEnum.Refunded
      ? {
          title: "Reversed",
          description: "This invoice has been reversed and the payment refunded.",
        }
      : null;
  const paymentPlan = getPaymentPlan(detail, row);
  const payments = detail?.payments || [];
  const hasPayments = payments.length > 0;
  const latestPayment = [...payments].sort(
    (first, second) => second.receivedAtUtcMilli - first.receivedAtUtcMilli
  )[0];
  const paymentCards = getPaymentOptionCards(detail, row, chamber);
  const isPaidLike =
    detail?.displayState === CmpInvoiceDisplayStateEnum.Paid ||
    detail?.displayState === CmpInvoiceDisplayStateEnum.Refunded;
  // Spec §4: the stamped invoiceKind classifies the document — the add-on
  // invoice shares sourceKind MEMBER_APPLICATION with the membership
  // application invoice, so kind must win (mirrors the backend PDF).
  const invoiceKind = detail?.invoice.invoiceKind ?? row.invoiceKind;
  const isAddOnInvoice = invoiceKind === CmpInvoiceKindEnum.AddOn;
  const isMembershipInvoice =
    invoiceKind === CmpInvoiceKindEnum.Application ||
    invoiceKind === CmpInvoiceKindEnum.Renewal;
  const isGroupInsuranceInvoice =
    getPaymentRules(detail, row)?.ruleKind ===
    CmpInvoicePaymentRuleKindEnum.InsuranceDirectDepositOnly;
  // Coverage fields only exist on newly built Application/AddOn invoices;
  // legacy invoices return null and omit the validity sentences gracefully.
  const hasCoveragePeriod =
    !!detail?.invoice.coveragePeriodStartUtcMilli &&
    !!detail?.invoice.coveragePeriodEndUtcMilli;
  const coveragePeriodText = `${formatOrdinalLongDate(
    detail?.invoice.coveragePeriodStartUtcMilli
  )} to ${formatOrdinalLongDate(detail?.invoice.coveragePeriodEndUtcMilli)}`;

  return (
    <div className="relative mx-auto w-full max-w-[1180px] rounded-md bg-white px-6 py-12 shadow-lg sm:px-10 lg:px-14">
      {watermark ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden">
          <div className="rotate-[-38deg] text-[120px] font-extrabold uppercase tracking-wide text-[#F31723] opacity-45 sm:text-[170px]">
            {watermark}
          </div>
        </div>
      ) : null}

      <div className="relative z-10">
        <div className="grid gap-10 lg:grid-cols-[1fr_420px]">
          <div className="text-[16px] leading-7 text-[#111827]">
            <div className="text-[20px] font-extrabold">
              {chamber?.logo ? (
                <img alt={`${chamber.name} logo`} className="mb-3 max-h-16 max-w-[220px]" src={chamber.logo} />
              ) : (
                "{Chamber Logo}"
              )}
            </div>
            <div className="text-[22px] font-extrabold">
              {chamber?.name || "Chamber of Commerce"}
            </div>
            {getChamberAddressLines(chamber).map((line) => (
              <div key={line}>{line}</div>
            ))}
            {chamber?.phoneNumber ? <div className="mt-2">☎ {chamber.phoneNumber}</div> : null}
            {chamber?.supportContactEmail ? <div>✉ {chamber.supportContactEmail}</div> : null}
          </div>

          <div className="text-left lg:text-right">
            <div className="text-[48px] font-extrabold leading-none tracking-[-0.02em] text-[#101827] sm:text-[58px]">
              {getInvoiceDocumentTitle({
                displayState: resolvedDisplayState,
                hasPayments,
              })}
            </div>
            <dl className="mt-6 space-y-3 text-[18px]">
              {[
                ["Invoice #:", row.invoiceNumber],
                ["Invoice Date:", formatDate(detail?.invoice.issuedAtUtcMilli || row.issuedAtUtcMilli)],
                ["Due Date:", formatDate(detail?.invoice.dueAtUtcMilli || row.dueAtUtcMilli)],
                // Spec §4: only membership (Application/Renewal) invoices
                // carry the plan-derived Billing Method row — the add-on and
                // event/custom invoices have none (matches the backend PDF).
                // Chamber-group-insurance invoices label the billing method
                // "Chamber Group Insurance" instead of the plan-derived value.
                ...(isMembershipInvoice
                  ? [
                      [
                        "Billing Method:",
                        isGroupInsuranceInvoice
                          ? "Chamber Group Insurance"
                          : paymentPlan?.enabled
                          ? "Monthly Payment"
                          : "Annual Payment",
                      ],
                    ]
                  : []),
              ].map(([label, value]) => (
                <div className="grid grid-cols-[150px_1fr] gap-4 lg:grid-cols-[1fr_190px]" key={label}>
                  <dt>{label}</dt>
                  <dd className="font-extrabold">{value}</dd>
                </div>
              ))}
            </dl>
            <div className="ml-auto mt-7 flex max-w-[300px] items-center justify-between rounded border-2 border-[#1684FF] bg-[#EEF4FF] px-7 py-4 text-[18px]">
              <span>Status:</span>
              <span className="font-extrabold text-[#1684FF]">{statusLabel}</span>
            </div>
          </div>
        </div>

        <div className="my-12 border-t border-gray-100" />

        <div className="grid min-w-0 gap-12 md:grid-cols-2">
          <SnapshotBlock
            fallbackName={row.customerName}
            label="Sold To:"
            snapshot={billingSnapshot}
          />
          <SnapshotBlock
            fallbackName={row.customerName}
            label="Bill To:"
            snapshot={billToSnapshot}
          />
        </div>

        {/*
          INV-TABLE-002: a status banner for cancelled (voided) and reversed
          (refunded) invoices, placed in-flow immediately before the line-items
          grid so the state is unmistakable even where the diagonal watermark is
          hard to read. The document-wide watermark above is kept per the mock.
        */}
        {statusBanner ? (
          <div className="mt-12 rounded-md border-2 border-[#F31723] bg-[#FEecec] px-8 py-5 text-center">
            <div className="text-[22px] font-extrabold uppercase tracking-[0.12em] text-[#C11119]">
              {statusBanner.title}
            </div>
            <div className="mt-1 text-[15px] font-semibold text-[#8A1015]">
              {statusBanner.description}
            </div>
          </div>
        ) : null}

        <div className="mt-12 overflow-hidden">
          <div className="grid grid-cols-[1fr_2.3fr_0.8fr_1.2fr_1.2fr] bg-[#0D8EF2] px-8 py-4 text-[13px] font-extrabold uppercase tracking-[0.08em] text-white">
            <div>Code</div>
            <div>Description</div>
            <div className="text-center">Qty</div>
            <div className="text-right">Unit Price</div>
            <div className="text-right">Total</div>
          </div>
          {(lineItems.length ? lineItems : []).map((item) => (
            <div
              className="grid grid-cols-[1fr_2.3fr_0.8fr_1.2fr_1.2fr] border-b border-gray-100 px-8 py-5 text-[18px] text-[#475569]"
              key={item.id}
            >
              <div className="min-w-0 break-words font-extrabold">{item.itemCode || "-"}</div>
              <div className="min-w-0 break-words">{item.description}</div>
              <div className="text-center">{item.quantity}</div>
              <div className="text-right">{formatCurrency(item.unitPriceInCents)}</div>
              <div className="text-right font-extrabold">
                {formatCurrency(item.totalAmountInCents)}
              </div>
            </div>
          ))}
          {!lineItems.length ? (
            <div className="border-b border-gray-100 px-8 py-6 text-[16px] text-gray-500">
              No line items available for this invoice.
            </div>
          ) : null}
          <div className="h-28 border-b border-gray-100" />
        </div>

        <div className="mt-12 grid gap-10 lg:grid-cols-[1fr_360px]">
          {/*
            Spec §4: coverage-period billing details beside the totals,
            mirroring the backend PDF's block verbatim. Membership invoices
            only render it when the coverage fields are present (legacy
            invoices predate them); the add-on invoice always carries its
            separate-billing sentence; other kinds have no details block.
          */}
          <div className="min-w-0 break-words text-[14px] leading-6">
            {isAddOnInvoice ? (
              <>
                <div className="text-[16px] font-extrabold">
                  Additional Benefits & Services Billing Details
                </div>
                {hasCoveragePeriod && (
                  <p className="mt-3">
                    Your Additional Benefit is valid from:
                    <br />
                    <span className="font-extrabold">{coveragePeriodText}</span>
                  </p>
                )}
                <p className={hasCoveragePeriod ? "mt-2" : "mt-3"}>
                  This invoice covers the additional membership benefits you
                  selected during registration. These charges are invoiced
                  separately from your chamber membership fees and are payable
                  in full.
                </p>
              </>
            ) : isGroupInsuranceInvoice ? (
              <div className="text-[16px] font-extrabold">
                Chamber Insurance Monthly Billing Enabled
              </div>
            ) : isMembershipInvoice && hasCoveragePeriod ? (
              <>
                <div className="text-[16px] font-extrabold">
                  Membership And Billing Method Details
                </div>
                <p className="mt-3">
                  Your Chamber Membership is valid from:
                  <br />
                  <span className="font-extrabold">{coveragePeriodText}</span>
                </p>
                <p className="mt-2">
                  {paymentPlan?.enabled
                    ? "You have opted to be billed monthly for your annual chamber membership."
                    : "You have opted to be billed annually for your chamber membership."}
                </p>
              </>
            ) : null}
          </div>

          <div className="min-w-0 space-y-3 break-words text-[18px]">
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span className="font-bold">{formatCurrency(subtotal)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-200 pb-3">
              <span>{taxLabel}</span>
              <span className="font-bold">{formatCurrency(tax)}</span>
            </div>
            <div className="flex items-end justify-between gap-4 text-[23px] font-extrabold">
              <span>TOTAL DUE</span>
              <span className="text-[34px]">{formatCurrency(total)}</span>
            </div>
            {latestPayment ? (
              <div className="flex justify-between border-t border-gray-200 pt-3 text-[16px] font-extrabold uppercase">
                <span>Payment {formatDate(latestPayment.receivedAtUtcMilli)}</span>
                <span>{formatCurrency(latestPayment.amountInCents)}</span>
              </div>
            ) : null}
            {paymentPlan?.enabled || isPaidLike ? (
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-end justify-between gap-4 text-[23px] font-extrabold">
                  <span>{amountDue > 0 ? "OUTSTANDING BALANCE" : "BALANCE"}</span>
                  <span className="text-[34px]">{formatCurrency(amountDue)}</span>
                </div>
                {paymentPlan?.enabled ? (
                  <div className="mt-2 border-t border-gray-200 pt-2 text-[14px] font-extrabold uppercase">
                    {Math.max(
                      0,
                      paymentPlan.totalInstallments - paymentPlan.paidInstallments
                    )}{" "}
                    installments remaining
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        <div className="mt-12 rounded-md border-2 border-[#1684FF] p-8">
          {isPaidLike || latestPayment ? (
            <div className="text-[22px] font-extrabold uppercase tracking-wide">
              Payment received via{" "}
              {latestPayment?.paymentMethodLabel ||
                getRecordedPaymentMethodLabel(row.paymentMethodLabel) ||
                "payment"}
            </div>
          ) : getPaymentRules(detail, row)?.ruleKind ===
            CmpInvoicePaymentRuleKindEnum.InsuranceDirectDepositOnly ? (
            <div className="text-[22px] font-extrabold uppercase tracking-wide">
              Payment options via direct deposit
            </div>
          ) : (
            <>
              <div className="mb-8 text-[22px] font-extrabold uppercase tracking-wide">
                Payment Options
              </div>
              <div className="grid min-w-0 gap-6 md:grid-cols-2 lg:grid-cols-5">
                {paymentCards.map((card) => (
                  <div
                    className="min-w-0 break-words border-r border-[#1684FF] pr-5 last:border-r-0"
                    key={card.label}
                  >
                    <div className="text-[19px] font-extrabold">{card.label}</div>
                    <p className="mt-4 whitespace-pre-line text-[14px] leading-5">{card.body}</p>
                    {card.cta ? (
                      <Button
                        sx={{
                          backgroundColor: invoiceBlue,
                          borderRadius: "4px",
                          color: "white",
                          fontSize: 18,
                          fontWeight: 800,
                          mt: 2,
                          px: 4,
                          textTransform: "none",
                          "&:hover": { backgroundColor: "#0B74D5" },
                        }}
                      >
                        {card.cta}
                      </Button>
                    ) : null}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="mt-10 grid min-w-0 gap-8 md:grid-cols-2">
          <div className="min-w-0 break-words text-[15px] leading-7">
            <div className="font-extrabold">Payment Terms</div>
            <p className="mt-4">
              Thank you for your support of the {chamber?.name || "chamber"}.
            </p>
            {/*
              Payment-terms note comes from the invoice's publicMemo (which the
              backend seeds from Chamber Setup's paymentTermsNote at creation).
              The create-only defaults query (paymentTermsNote) is not threaded
              into this modal, so we render the invoice value when present and
              nothing otherwise rather than showing a placeholder token.
            */}
            {detail?.invoice.publicMemo ? (
              <p>{detail.invoice.publicMemo}</p>
            ) : null}
          </div>
          <div className="min-w-0 break-words rounded-md border border-gray-200 p-6 text-[16px] leading-8">
            <div className="font-extrabold">Billing inquiries contact</div>
            {chamber?.supportContactEmail ? <div className="mt-3">✉ {chamber.supportContactEmail}</div> : null}
            {chamber?.phoneNumber ? <div>☎ {chamber.phoneNumber}</div> : null}
          </div>
        </div>

        <div className="mt-12 border-t border-gray-100 pt-4 text-center text-[18px] font-bold">
          {chamber?.name || "Chamber of Commerce"}
          {detail?.taxRegistrationNumber
            ? ` · GST/HST #: ${detail.taxRegistrationNumber}`
            : ""}
        </div>
        <div className="mt-6 border-t border-gray-100 pt-4 text-center text-[14px]">
          Thank you for being a valued member of the {chamber?.name || "chamber"}.
        </div>
      </div>
    </div>
  );
};

export const InvoicePreviewModal = ({
  actionLoading,
  canApplyPayment,
  canPrintPdf,
  canSendInvoice,
  chamber,
  detail,
  errorMessage,
  loading,
  onAddPayment,
  onApproveSave,
  onApproveSend,
  onClose,
  onPrintPdf,
  onSendInvoice,
  onSendReceipt,
  open,
  row,
  sendIsResend,
}: {
  actionLoading: boolean;
  canApplyPayment: boolean;
  canPrintPdf: boolean;
  canSendInvoice: boolean;
  chamber?: ChamberInfo;
  detail?: InvoiceDetail | null;
  errorMessage?: string | null;
  loading: boolean;
  onAddPayment: () => void;
  onApproveSave: () => void;
  onApproveSend: () => void;
  onClose: () => void;
  onPrintPdf: () => void;
  onSendInvoice: () => void;
  onSendReceipt: () => void;
  open: boolean;
  row: InvoiceRow | null;
  sendIsResend: boolean;
}) => {
  const handlers: Record<InvoicePreviewAction["key"], () => void> = {
    printPdf: onPrintPdf,
    addPayment: onAddPayment,
    approveSave: onApproveSave,
    approveSend: onApproveSend,
    sendInvoice: onSendInvoice,
    sendReceipt: onSendReceipt,
  };

  // Only trust `detail` once it belongs to the row currently being previewed —
  // Apollo can briefly surface the previously-opened invoice's detail while the
  // new detail query is in flight. Until it matches, drive the action bar off the
  // row alone so the buttons never reflect a stale invoice.
  const detailForRow = row && detail?.invoice.id === row.id ? detail : null;
  const actions = row
    ? getInvoicePreviewActions({
        displayState: detailForRow?.displayState || row.displayState,
        hasPayments: (detailForRow?.payments?.length || 0) > 0,
        ruleKind:
          detailForRow?.invoice.paymentRules?.ruleKind ||
          row.paymentRules?.ruleKind,
        canApplyPayment,
        canSendInvoice,
        sendIsResend,
      })
    : [];

  return (
    <Dialog
      fullScreen
      open={open}
      PaperProps={{ sx: { backgroundColor: "#EEF3F9" } }}
      onClose={onClose}
    >
      <div className="min-h-screen bg-[#EEF3F9] px-4 py-8 sm:px-7">
        <IconButton
          aria-label="Close invoice preview"
          onClick={onClose}
          sx={{
            color: "#6B4C3D",
            position: "fixed",
            right: 18,
            top: 12,
            zIndex: 10,
          }}
        >
          <CloseIcon />
        </IconButton>
        <div className="mx-auto mb-10 flex max-w-[1180px] flex-wrap justify-end gap-5 pr-8">
          {actions.map((action) => {
            const isPrint = action.key === "printPdf";
            const button = (
              <InvoiceActionButton
                color={action.color}
                compact={isPrint}
                disabled={actionLoading || (isPrint && !canPrintPdf)}
                icon={isPrint ? <LocalPrintshopOutlinedIcon /> : undefined}
                onClick={handlers[action.key]}
              >
                {action.label}
              </InvoiceActionButton>
            );

            if (!action.caption && !action.withEmailPreview) {
              return (
                <React.Fragment key={action.key}>{button}</React.Fragment>
              );
            }

            return (
              <div key={action.key} className="max-w-[240px]">
                {button}
                {action.caption ? (
                  <p className="mt-2 text-center text-[11px] font-medium leading-4 text-gray-500">
                    {action.caption}
                  </p>
                ) : null}
                {action.withEmailPreview ? (
                  <div className="mt-2 text-center text-[15px] font-extrabold">
                    <span className="inline-flex items-center gap-1">
                      <EmailOutlinedIcon fontSize="small" /> Preview Email
                    </span>
                    <div className="text-[12px] font-medium">
                      {action.previewSubline || "To be sent with invoice"}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
        {errorMessage ? (
          <Alert className="mx-auto mb-6 max-w-[1180px]" severity="error">
            {errorMessage}
          </Alert>
        ) : null}
        {loading && !detail ? (
          <div className="mx-auto flex max-w-[1180px] items-center justify-center gap-3 rounded bg-white p-10 text-sm font-semibold text-gray-500">
            <CircularProgress size={20} />
            Loading invoice preview...
          </div>
        ) : row ? (
          <InvoicePaper chamber={chamber} detail={detail} row={row} />
        ) : null}
      </div>
    </Dialog>
  );
};

const PaymentSpecBox = ({ label, value }: { label: string; value: string }) => (
  <div>
    <div className="mb-3 text-[14px] font-extrabold uppercase tracking-wide">
      {label}
    </div>
    <div className="rounded-md border-2 border-black px-7 py-5 text-[38px] font-extrabold leading-none">
      {value}
    </div>
  </div>
);

const PaymentMethodCard = ({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) => (
  <button
    className={`min-h-[116px] border-2 p-4 text-left ${
      active ? "border-[#1684FF]" : "border-transparent"
    }`}
    type="button"
    onClick={onClick}
  >
    <div className="mb-4 text-[16px] font-extrabold">{label}</div>
    <span
      className={`inline-flex min-w-[110px] justify-center rounded px-5 py-2 text-[16px] font-extrabold ${
        active
          ? "bg-[#1684FF] text-white"
          : "border border-[#1684FF] bg-white text-[#1684FF]"
      }`}
    >
      Select
    </span>
  </button>
);

export const ProcessPaymentModal = ({
  actionError,
  detail,
  loading,
  onClose,
  onRecordManualPayment,
  onStartCardCheckout,
  open,
  recordingManualPayment,
  row,
  startingCardCheckout,
}: {
  actionError?: string | null;
  detail?: InvoiceDetail | null;
  loading: boolean;
  onClose: () => void;
  onRecordManualPayment: (input: RecordInvoicePaymentInput) => Promise<boolean>;
  onStartCardCheckout: () => Promise<boolean>;
  open: boolean;
  recordingManualPayment: boolean;
  row: InvoiceRow | null;
  startingCardCheckout: boolean;
}) => {
  const methodOptions = useMemo(() => buildPaymentMethodOptions(detail, row), [detail, row]);
  const [selectedMethodLabel, setSelectedMethodLabel] = useState("");
  const [paymentDate, setPaymentDate] = useState(formatDateInput(Date.now()));
  const [amountInput, setAmountInput] = useState("0.00");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  const amountInCents = parseCurrencyInput(amountInput);
  const rules = getPaymentRules(detail, row);
  const paymentPlan = getPaymentPlan(detail, row);
  const shouldShowPaymentSpec =
    !!paymentPlan?.enabled ||
    rules?.ruleKind === CmpInvoicePaymentRuleKindEnum.InsuranceDirectDepositOnly;
  const outstandingSubtotal = getInvoiceSubtotal(detail, row);
  const outstandingTax = getInvoiceTax(detail);
  const outstandingBalance = getAmountDue(detail, row);
  const splitPayment = splitAmountByTaxRatio(amountInCents, detail, row);
  const remainingBalance = Math.max(0, outstandingBalance - amountInCents);
  const selectedMethod =
    methodOptions.find((method) => method.mutationLabel === selectedMethodLabel) || null;
  const isCreditCardSelected = Boolean(selectedMethod?.isCreditCard);
  const processing = recordingManualPayment || startingCardCheckout;

  useEffect(() => {
    if (!open) {
      return;
    }

    const nextAmount = getNextPaymentAmount(detail, row);

    setSelectedMethodLabel(methodOptions[0]?.mutationLabel || "");
    setPaymentDate(formatDateInput(Date.now()));
    setAmountInput(formatCurrencyInput(nextAmount));
    setReference("");
    setNote("");
    setConfirmOpen(false);
    setSuccessOpen(false);
  }, [detail, methodOptions, open, row]);

  const handleConfirmPayment = async () => {
    if (!selectedMethod || amountInCents <= 0) {
      return;
    }

    if (isCreditCardSelected) {
      await onStartCardCheckout();
      return;
    }

    const succeeded = await onRecordManualPayment({
      amountInCents,
      note: note.trim() || undefined,
      paymentMethodLabel: selectedMethod.mutationLabel,
      receivedAtUtcMilli: dateInputToUtcMilli(paymentDate),
      reference: reference.trim() || undefined,
    });

    if (succeeded) {
      setConfirmOpen(false);
      setSuccessOpen(true);
    }
  };

  return (
    <>
      <Dialog
        fullWidth
        maxWidth={false}
        open={open}
        PaperProps={{
          sx: {
            borderRadius: "8px",
            maxWidth: 1580,
            width: "calc(100% - 48px)",
          },
        }}
        onClose={onClose}
      >
        <div className="relative bg-white px-8 py-9 sm:px-10">
          <IconButton
            aria-label="Close process payment"
            onClick={onClose}
            sx={{ color: "#6B4C3D", position: "absolute", right: 16, top: 12 }}
          >
            <CloseIcon />
          </IconButton>
          <h2 className="text-[32px] font-extrabold text-black">Process Payment</h2>

          {loading && !detail ? (
            <div className="my-8 flex items-center gap-3 text-sm font-semibold text-gray-500">
              <CircularProgress size={20} />
              Loading invoice payment details...
            </div>
          ) : null}

          {actionError ? (
            <Alert className="mt-5" severity="error">
              {actionError}
            </Alert>
          ) : null}

          {row ? (
            <div className="mt-8 space-y-7">
              <div>
                <div className="text-[15px] font-extrabold uppercase text-[#1F2937]">
                  Bill To
                </div>
                <div className="mt-3 text-[20px] font-extrabold text-[#111827]">
                  {getBillToSnapshot(detail)?.customerName || row.customerName}
                </div>
              </div>

              <div className="border-t border-gray-100 pt-5">
                <div className="text-[22px] font-extrabold">Reference Invoice</div>
                <div className="mt-2 text-[28px] font-extrabold">{row.invoiceNumber}</div>
              </div>

              {shouldShowPaymentSpec ? (
                <div className="grid gap-8">
                  <div className="grid gap-8 lg:grid-cols-[1.45fr_0.8fr_1.05fr]">
                    <PaymentSpecBox
                      label="Outstanding Sub Total"
                      value={formatCurrency(outstandingSubtotal)}
                    />
                    <PaymentSpecBox
                      label="Applicable Tax"
                      value={formatCurrency(outstandingTax)}
                    />
                    <PaymentSpecBox
                      label="Outstanding Balance Owing"
                      value={formatCurrency(outstandingBalance)}
                    />
                  </div>
                  <div className="max-w-[730px]">
                    <div className="mb-3 text-[14px] font-extrabold uppercase tracking-wide">
                      Payment Date
                    </div>
                    <TextField
                      fullWidth
                      type="date"
                      value={paymentDate}
                      onChange={({ target }) => setPaymentDate(target.value)}
                    />
                  </div>
                  <div className="grid gap-8 lg:grid-cols-3">
                    <PaymentSpecBox
                      label="Sub Total Payment"
                      value={formatCurrency(splitPayment.subtotalInCents)}
                    />
                    <PaymentSpecBox
                      label="Applicable Tax Payment"
                      value={formatCurrency(splitPayment.taxInCents)}
                    />
                    <PaymentSpecBox
                      label="Total Payment"
                      value={formatCurrency(amountInCents)}
                    />
                  </div>
                  <div className="max-w-[1180px]">
                    <PaymentSpecBox
                      label="Remaining Outstanding Balance"
                      value={formatCurrency(remainingBalance)}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <div className="text-[14px] font-extrabold uppercase tracking-wide">
                      Remaining Balance Owing
                    </div>
                    <div className="mt-3 rounded-md border-2 border-black px-6 py-4 text-[34px] font-extrabold">
                      {formatCurrency(outstandingBalance)}
                    </div>
                  </div>
                  <div className="grid gap-6 sm:grid-cols-2">
                    <TextField
                      fullWidth
                      label="Payment Date"
                      type="date"
                      value={paymentDate}
                      onChange={({ target }) => setPaymentDate(target.value)}
                    />
                    <TextField
                      fullWidth
                      label="Amount to Pay"
                      value={amountInput}
                      onChange={({ target }) => setAmountInput(target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                    />
                  </div>
                </>
              )}

              <div className="rounded-md border-2 border-[#1684FF] p-7">
                <div className="mb-6 text-[16px] font-extrabold uppercase tracking-[0.08em] text-[#1684FF]">
                  Select Payment Method
                </div>
                {methodOptions.length ? (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                    {methodOptions.map((method) => (
                      <PaymentMethodCard
                        active={selectedMethodLabel === method.mutationLabel}
                        key={method.mutationLabel}
                        label={method.displayLabel}
                        onClick={() => setSelectedMethodLabel(method.mutationLabel)}
                      />
                    ))}
                  </div>
                ) : (
                  <Alert severity="warning">
                    No enabled payment method is available for this invoice. Check Chamber Setup.
                  </Alert>
                )}
              </div>

              <TextField
                fullWidth
                label="Reference Number (Cheque # or Transaction ID)"
                placeholder="Enter Reference number and details"
                value={reference}
                onChange={({ target }) => setReference(target.value)}
              />
              <TextField
                fullWidth
                label="Internal Notes"
                minRows={3}
                multiline
                placeholder="Additional details for bookkeeping..."
                value={note}
                onChange={({ target }) => setNote(target.value)}
              />

              <div className="flex flex-col gap-4 bg-[#F1F6FF] p-5 sm:flex-row">
                <Button
                  fullWidth
                  disabled={!selectedMethod || amountInCents <= 0 || processing}
                  onClick={() => setConfirmOpen(true)}
                  sx={{
                    backgroundColor: invoiceBlue,
                    boxShadow: "0 5px 10px rgba(22, 132, 255, 0.28)",
                    color: "white",
                    fontSize: 17,
                    minHeight: 56,
                    textTransform: "none",
                    "&:hover": { backgroundColor: "#0B74D5" },
                  }}
                >
                  Confirm & Record Payment
                </Button>
                <Button
                  disabled={processing}
                  onClick={onClose}
                  sx={{
                    border: "1px solid #111827",
                    color: "#111827",
                    fontSize: 17,
                    minHeight: 56,
                    minWidth: 120,
                    textTransform: "none",
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <div className="relative w-[560px] max-w-[calc(100vw-32px)] rounded bg-white px-12 py-14 text-center">
          <IconButton
            aria-label="Close payment confirmation"
            onClick={() => setConfirmOpen(false)}
            sx={{
              backgroundColor: invoiceRed,
              color: "white",
              position: "absolute",
              right: 16,
              top: 14,
              "&:hover": { backgroundColor: "#D90F1A" },
            }}
          >
            <CloseIcon />
          </IconButton>
          <div className="mx-auto flex h-28 w-28 items-center justify-center rounded-full bg-[#F31723] text-white">
            <QuestionMarkOutlinedIcon sx={{ fontSize: 56 }} />
          </div>
          <div className="mt-12 text-[28px] font-extrabold leading-[1.65]">
            Are you sure you want to process payment for this invoice?
          </div>
          {isCreditCardSelected ? (
            <p className="mt-4 text-sm text-gray-600">
              Stripe Checkout will open. CMP will only mark the invoice paid after Stripe confirms the payment.
            </p>
          ) : null}
          <div className="mt-9 grid gap-6 sm:grid-cols-2">
            <Button
              disabled={processing}
              onClick={handleConfirmPayment}
              sx={{
                backgroundColor: invoiceBlue,
                boxShadow: "0 4px 10px rgba(15, 23, 42, 0.22)",
                color: "white",
                fontSize: 17,
                fontWeight: 900,
                minHeight: 56,
                textTransform: "uppercase",
                "&:hover": { backgroundColor: "#0B74D5" },
              }}
            >
              Proceed
            </Button>
            <Button
              disabled={processing}
              onClick={() => setConfirmOpen(false)}
              sx={{
                backgroundColor: "#24272D",
                color: "white",
                fontSize: 17,
                fontWeight: 900,
                minHeight: 56,
                textTransform: "uppercase",
                "&:hover": { backgroundColor: "#111827" },
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Dialog>

      <Dialog open={successOpen} onClose={() => setSuccessOpen(false)}>
        <div className="relative w-[560px] max-w-[calc(100vw-32px)] rounded bg-white px-12 py-20 text-center">
          <IconButton
            aria-label="Close payment success"
            onClick={() => {
              setSuccessOpen(false);
              onClose();
            }}
            sx={{
              backgroundColor: invoiceRed,
              color: "white",
              position: "absolute",
              right: 16,
              top: 14,
              "&:hover": { backgroundColor: "#D90F1A" },
            }}
          >
            <CloseIcon />
          </IconButton>
          <div className="mx-auto flex h-28 w-28 items-center justify-center rounded-full bg-[#16B983] text-white">
            <CheckCircleOutlineIcon sx={{ fontSize: 64 }} />
          </div>
          <div className="mt-12 text-[28px] font-extrabold leading-[1.65]">
            Your payment has been processed successfully!
          </div>
        </div>
      </Dialog>
    </>
  );
};

// INV-PREVIEW-003: shared green-check success dialog reusing the ProcessPaymentModal
// success pattern. Rendered by ManageInvoices after a successful send so the
// confirmation is visible ABOVE the fullscreen invoice preview dialog (a plain
// inline success Alert sits behind that dialog and is never seen).
export const InvoiceSuccessDialog = ({
  message,
  onClose,
  open,
}: {
  message: string;
  onClose: () => void;
  open: boolean;
}) => (
  <Dialog open={open} onClose={onClose} sx={{ zIndex: 2000 }}>
    <div className="relative w-[560px] max-w-[calc(100vw-32px)] rounded bg-white px-12 py-20 text-center">
      <IconButton
        aria-label="Close success message"
        onClick={onClose}
        sx={{
          backgroundColor: invoiceRed,
          color: "white",
          position: "absolute",
          right: 16,
          top: 14,
          "&:hover": { backgroundColor: "#D90F1A" },
        }}
      >
        <CloseIcon />
      </IconButton>
      <div className="mx-auto flex h-28 w-28 items-center justify-center rounded-full bg-[#16B983] text-white">
        <CheckCircleOutlineIcon sx={{ fontSize: 64 }} />
      </div>
      <div className="mt-12 text-[28px] font-extrabold leading-[1.65]">{message}</div>
    </div>
  </Dialog>
);
